export type WeekdayId = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export interface DayAvailability {
  id: WeekdayId;
  label: string;
  enabled: boolean;
  start: string;
  end: string;
}

export interface CalendarException {
  id: string;
  date: string;
  label: string;
  unavailable: boolean;
  start: string;
  end: string;
}

export interface TransferException {
  id: string;
  date: string;
  label: string;
  unavailable: boolean;
  start: string;
  end: string;
}

export interface CalendarAvailability {
  connected: boolean;
  calendarId: string;
  timezone: string;
  weeklySchedule: DayAvailability[];
  appointmentDurationMinutes: number;
  beforeBufferMinutes: number;
  afterBufferMinutes: number;
  minimumNoticeHours: number;
  bookingHorizonDays: number;
  exceptions: CalendarException[];
  appointmentType: string;
  leadTypes: string[];
  inviteTitleTemplate: string;
  inviteDescriptionTemplate: string;
}

export interface TransferAvailability {
  destinationNumber: string;
  timezone: string;
  weeklySchedule: DayAvailability[];
  exceptions: TransferException[];
  temporarilyUnavailable: boolean;
  leadTypes: string[];
  ringTimeoutSeconds: number;
  backupNumber: string;
  maximumSimultaneousTransfers: number;
  bookWhenUnavailable: boolean;
}

export interface AvailabilitySettings {
  calendar: CalendarAvailability;
  transfers: TransferAvailability;
}

export type AvailabilityDecisionKind = 'live-transfer' | 'booking' | 'held';

export interface AvailabilityDecision {
  kind: AvailabilityDecisionKind;
  label: string;
  detail: string;
  qualified: boolean;
  insideTransferWindow: boolean;
  acceptingTransfers: boolean;
}

export const WEEKDAYS: { id: WeekdayId; label: string }[] = [
  { id: 'monday', label: 'Monday' },
  { id: 'tuesday', label: 'Tuesday' },
  { id: 'wednesday', label: 'Wednesday' },
  { id: 'thursday', label: 'Thursday' },
  { id: 'friday', label: 'Friday' },
  { id: 'saturday', label: 'Saturday' },
  { id: 'sunday', label: 'Sunday' },
];

export const CALENDAR_PREVIEW_DATE = {
  iso: '2026-07-14',
  label: 'Tuesday, July 14',
  weekdayId: 'tuesday' as WeekdayId,
} as const;

function defaultWeek(start: string, end: string): DayAvailability[] {
  return WEEKDAYS.map((day, index) => ({ ...day, enabled: index < 5, start, end }));
}

export function createDefaultAvailabilitySettings(timezone: string, destinationNumber: string): AvailabilitySettings {
  return {
    calendar: {
      connected: true,
      calendarId: 'primary',
      timezone,
      weeklySchedule: defaultWeek('09:00', '17:00'),
      appointmentDurationMinutes: 30,
      beforeBufferMinutes: 10,
      afterBufferMinutes: 15,
      minimumNoticeHours: 4,
      bookingHorizonDays: 21,
      exceptions: [
        { id: 'cal-ex-1', date: '2026-07-17', label: 'Team training', unavailable: true, start: '09:00', end: '17:00' },
      ],
      appointmentType: 'Coverage consultation',
      leadTypes: ['Mortgage protection', 'Qualified callback'],
      inviteTitleTemplate: '{{lead.first_name}} + {{agent.name}} · Coverage consultation',
      inviteDescriptionTemplate: 'ACL Auto qualified {{lead.first_name}} from {{lead.state}}. Review their request and the conversation summary before the call.',
    },
    transfers: {
      destinationNumber,
      timezone,
      weeklySchedule: defaultWeek('09:00', '18:00'),
      exceptions: [
        { id: 'transfer-ex-1', date: '2026-07-16', label: 'Closes early', unavailable: false, start: '09:00', end: '14:00' },
      ],
      temporarilyUnavailable: false,
      leadTypes: ['Mortgage protection', 'High-intent callback'],
      ringTimeoutSeconds: 24,
      backupNumber: '(888) 555-0140',
      maximumSimultaneousTransfers: 2,
      bookWhenUnavailable: true,
    },
  };
}

export function cloneAvailabilitySettings(settings: AvailabilitySettings): AvailabilitySettings {
  return {
    calendar: {
      ...settings.calendar,
      weeklySchedule: settings.calendar.weeklySchedule.map((day) => ({ ...day })),
      exceptions: settings.calendar.exceptions.map((exception) => ({ ...exception })),
      leadTypes: [...settings.calendar.leadTypes],
    },
    transfers: {
      ...settings.transfers,
      weeklySchedule: settings.transfers.weeklySchedule.map((day) => ({ ...day })),
      exceptions: settings.transfers.exceptions.map((exception) => ({ ...exception })),
      leadTypes: [...settings.transfers.leadTypes],
    },
  };
}

function timeToMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
}

export function isInsideTransferSchedule(settings: AvailabilitySettings, dayId: WeekdayId = 'tuesday', time = '11:15'): boolean {
  const day = settings.transfers.weeklySchedule.find((candidate) => candidate.id === dayId);
  if (!day?.enabled) return false;
  const minute = timeToMinutes(time);
  return minute >= timeToMinutes(day.start) && minute <= timeToMinutes(day.end);
}

export function evaluateAvailabilityDecision(settings: AvailabilitySettings, paused: boolean): AvailabilityDecision {
  const qualified = true;
  const insideTransferWindow = isInsideTransferSchedule(settings);
  const acceptingTransfers = !paused && !settings.transfers.temporarilyUnavailable && Boolean(settings.transfers.destinationNumber.trim());
  if (paused) {
    return { kind: 'held', label: 'Outreach held', detail: 'This workspace is paused. No transfer or booking action is started.', qualified, insideTransferWindow, acceptingTransfers: false };
  }
  if (qualified && insideTransferWindow && acceptingTransfers) {
    return { kind: 'live-transfer', label: 'Live transfer', detail: `Ring ${settings.transfers.destinationNumber} for up to ${settings.transfers.ringTimeoutSeconds} seconds.`, qualified, insideTransferWindow, acceptingTransfers };
  }
  if (settings.transfers.bookWhenUnavailable && settings.calendar.connected) {
    return { kind: 'booking', label: 'Book appointment', detail: `Offer ${settings.calendar.appointmentDurationMinutes}-minute slots from ${settings.calendar.calendarId === 'primary' ? 'Primary calendar' : 'Team calendar'}.`, qualified, insideTransferWindow, acceptingTransfers };
  }
  return { kind: 'held', label: 'Hold for client', detail: 'Transfer is unavailable and calendar fallback is disabled or disconnected.', qualified, insideTransferWindow, acceptingTransfers };
}

export function previewCalendarSlots(settings: AvailabilitySettings): string[] {
  if (!settings.calendar.connected) return [];
  const day = settings.calendar.weeklySchedule.find((candidate) => candidate.id === CALENDAR_PREVIEW_DATE.weekdayId && candidate.enabled);
  if (!day) return [];

  const exception = settings.calendar.exceptions.find((candidate) => candidate.date === CALENDAR_PREVIEW_DATE.iso);
  if (exception?.unavailable) return [];

  const windowStart = timeToMinutes(exception?.start ?? day.start);
  const windowEnd = timeToMinutes(exception?.end ?? day.end);
  const occupiedMinutes = settings.calendar.beforeBufferMinutes
    + settings.calendar.appointmentDurationMinutes
    + settings.calendar.afterBufferMinutes;
  const slots: string[] = [];

  for (let occupiedStart = windowStart; slots.length < 4; occupiedStart += Math.max(occupiedMinutes, 1)) {
    const minute = occupiedStart + settings.calendar.beforeBufferMinutes;
    const occupiedEnd = minute + settings.calendar.appointmentDurationMinutes + settings.calendar.afterBufferMinutes;
    if (occupiedEnd > windowEnd) break;
    const hours = Math.floor(minute / 60);
    const minutes = minute % 60;
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    slots.push(`${displayHour}:${String(minutes).padStart(2, '0')} ${suffix}`);
  }

  return slots;
}
